import React, { useEffect, useMemo, useRef, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { useParams } from 'react-router-dom';
import { FaArrowRight, FaGift } from 'react-icons/fa';

import { client } from '../client';
import Countdown from './components/countdown';
import BirthdayCelebration from './components/birthday-celebration';
import Confetti from './components/confetti';
import FloatingHearts from './components/floating-hearts';
import Loader from './components/Loader';
import { BIRTHDAY_FIXED_MUSIC, BIRTHDAY_THEME_COPY } from '../birthday/constants';
import './index.css';

const BirthdayView = () => {
  const { birthdayId } = useParams();
  const [page, setPage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isCelebration, setIsCelebration] = useState(false);
  const [showOpenButton, setShowOpenButton] = useState(false);
  const [bubbles, setBubbles] = useState([]);
  const audioRef = useRef(null);

  const decodedBirthdayId = decodeURIComponent(birthdayId || '');
  const themeCopy = useMemo(
    () => BIRTHDAY_THEME_COPY[page?.theme] || BIRTHDAY_THEME_COPY.romantic,
    [page?.theme]
  );
  const targetDate = useMemo(
    () => (page?.birthdayDate ? new Date(page.birthdayDate) : new Date()),
    [page?.birthdayDate]
  );

  useEffect(() => {
    if (!decodedBirthdayId) {
      setLoading(false);
      return;
    }
    client
      .fetch('*[_type == "birthdayPage" && _id == $birthdayId][0]', { birthdayId: decodedBirthdayId })
      .then((data) => setPage(data || null))
      .finally(() => {
        setTimeout(() => setLoading(false), 1200);
      });
  }, [decodedBirthdayId]);

  useEffect(() => {
    const generated = Array.from({ length: 18 }).map((_, index) => ({
      id: index,
      left: `${Math.random() * 100}%`,
      top: `${Math.random() * 100}%`,
      size: 14 + Math.floor(Math.random() * 10),
      duration: 3 + Math.random() * 2,
      delay: Math.random() * 5,
    }));
    setBubbles(generated);
  }, []);

  const startCelebration = () => {
    setShowOpenButton(false);
    setIsCelebration(true);
    if (audioRef.current) {
      audioRef.current.volume = 0.8;
      audioRef.current.play().catch((error) => {
        console.log('Autoplay prevented', error);
      });
    }
  };

  if (loading) {
    return <Loader />;
  }

  if (!page || page.status !== 'published') {
    return (
      <main className="birthday-main birthday-unavailable">
        <div className="birthday-main-card">
          <h2>Birthday page unavailable</h2>
          <p>This birthday page does not exist or is no longer published.</p>
        </div>
      </main>
    );
  }

  return (
    <main className={`birthday-main birthday-theme-${page.theme || 'romantic'}`}>
      {isCelebration ? <Confetti /> : null}
      <FloatingHearts />

      <motion.div
        className="birthday-main-wrap"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8 }}
      >
        <motion.div className="birthday-main-card" initial={{ scale: 0.92 }} animate={{ scale: 1 }}>
          <AnimatePresence mode="wait">
            {isCelebration ? (
              <BirthdayCelebration
                key="birthday-celebration"
                recipientName={page.recipientName}
                headline={page.headline}
                message={page.message}
                closingMessage={page.closingMessage}
              />
            ) : (
              <Countdown
                key="birthday-countdown"
                targetDate={targetDate}
                title={themeCopy.countdownTitle}
                helperText={`A special birthday page for ${page.recipientName}`}
                onCountdownEnd={() => setShowOpenButton(true)}
              />
            )}
          </AnimatePresence>
        </motion.div>
      </motion.div>

      {showOpenButton ? (
        <motion.div
          className="birthday-open-row"
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0 }}
        >
          <motion.button
            type="button"
            onClick={startCelebration}
            className="birthday-open-btn"
            whileTap={{ scale: 0.95 }}
            animate={{ y: [0, -4, 0], scale: [1, 1.03, 1] }}
            transition={{ duration: 1.8, repeat: Infinity }}
          >
            <FaGift />
            <span>{themeCopy.revealButton}</span>
            <FaArrowRight />
          </motion.button>
        </motion.div>
      ) : null}

      <audio ref={audioRef} src={BIRTHDAY_FIXED_MUSIC} preload="auto" loop />

      <div className="birthday-bubbles-layer">
        {bubbles.map((bubble) => (
          <motion.div
            key={bubble.id}
            className="birthday-bubble"
            style={{ left: bubble.left, top: bubble.top, width: `${bubble.size}px`, height: `${bubble.size}px` }}
            animate={{ y: [0, -20, 0], scale: [1, 1.1, 1] }}
            transition={{ duration: bubble.duration, repeat: Infinity, delay: bubble.delay }}
          />
        ))}
      </div>
    </main>
  );
};

export default BirthdayView;
